import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import { getI18nKeyOverrideForProductCard } from '../components/merch-card/I18nUtils';
import { ProductCard } from '../components/merch-card/cards/product-card/ProductCard';
import MobileBundleFeatureTable from '../components/mobile/MobileBundleFeatureTable';
import MobileMerchCardSelectorButtons from '../components/mobile/MobileMerchCardSelectorButtons';
import MobileCardWrapper from '../components/mobile/containers/MobileCardWrapper';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { MARKETING_PLUS_PAGE, MARKETING_PLUS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { getMobileMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { bundleProductsForBundleTypeSelector } from '../selectors/ProductsSelectors';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { MARKETING_PLUS_CARD, MARKETING_PLUS_PAGE_FEATURE_TABLE } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import { isValidApiName } from '../v2/utils/typeguards';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MobileMarketingPlusPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const productLevels = getMobileMerchCardsToShow(MARKETING_PLUS_SUITE);
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const highlightedProducts = useAppSelector(state => {
    if (!isAppReady) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    });
  });
  const productLevelToHighlightedProductMap = productLevels.reduce((acc, productLevel) => {
    var _highlightedProducts$;
    const apiName = (_highlightedProducts$ = highlightedProducts.find(product => product.productLevel === productLevel)) === null || _highlightedProducts$ === void 0 ? void 0 : _highlightedProducts$.apiName;
    if (apiName) {
      acc[productLevel] = apiName;
    }
    return acc;
  }, {});
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const apiName = productLevelToHighlightedProductMap[selectedProductLevel];
  const i18nKeys = getI18nKeyOverrideForProductCard(apiName);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: MARKETING_PLUS_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs(MobileWrapper, {
      navItem: navItem,
      navMarkerName: "MARKETING_PLUS_PAGE",
      children: [/*#__PURE__*/_jsx(BannerSection, {
        screen: screen
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          nonProductIdentifier: navItem
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          nonProductIdentifier: navItem
        })]
      }), productLevelToHighlightedProductMap[selectedProductLevel] && /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(MobileMerchCardSelectorButtons, {
          options: productLevels,
          onClick: setSelectedProductLevel,
          selectedOption: selectedProductLevel
        }), /*#__PURE__*/_jsx(MobileCardWrapper, {
          children: /*#__PURE__*/_jsx(ProductCard, {
            apiName: apiName,
            Title: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Title,
            Description: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Description,
            screen: screen,
            columnStart: 1,
            trackingSource: MARKETING_PLUS_CARD
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "800",
          children: /*#__PURE__*/_jsx(CreditsCallout, {
            navItem: screen
          })
        }), /*#__PURE__*/_jsx(MobileBundleFeatureTable, {
          apiNames: highlightedProducts.map(({
            apiName: highlightedProduct
          }) => highlightedProduct).filter(isValidApiName),
          productLevel: selectedProductLevel,
          screen: screen,
          trackingSource: MARKETING_PLUS_PAGE_FEATURE_TABLE
        })]
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: "marketingplus-footer",
        screen: screen,
        apiName: "marketing-plus-professional-bundle"
      })]
    })
  });
};
export default withErrorBoundary(MobileMarketingPlusPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: MARKETING_PLUS_PAGE,
  severity: ERROR_SEVERITY.HIGH
});